import {
    Autocomplete,
    AutocompleteRenderGroupParams,
    AutocompleteRenderInputParams,
    Box,
    createFilterOptions,
    List,
    ListItem,
    Stack,
    Typography,
} from "@mui/material";
import { ChevronDownIcon, CloseSmallIcon } from "src/foundations/icons";
import { BaseDropdownOption, BaseDropdownProps } from "src/components/inputs/dropdown/commonTypes";
import { forwardRef, HTMLAttributes, useEffect, useState } from "react";
import { AutocompleteProps } from "@mui/material/Autocomplete/Autocomplete";
import { SenecTextField } from "src/components/inputs/input-field/SenecTextField";
import { SenecCheckbox } from "src/components/inputs/senec-checkbox/SenecCheckbox";

export type MultiselectDropdownProps = {
    /**
     * Variant of textfield "outlined" or "filled"
     */
    variant?: "outlined" | "filled";
    /**
     * Size of textfield "small" or "medium"
     */
    size?: "small" | "medium";
    /**
     * Is dropdown required
     */
    required?: boolean;
    /**
     * Array of options.
     */
    options: (string | BaseDropdownOption)[];
    "data-testid"?: string;
} & BaseDropdownProps &
    Omit<
        AutocompleteProps<BaseDropdownOption | string, boolean | undefined, boolean | undefined, boolean | undefined>,
        "renderInput" | "onChange"
    >;

/**
 * Component for a custom drop down selection field.
 * - Based on MUI TextField.
 */
export const MultiselectDropdown = forwardRef(({ error, helperText, ...props }: MultiselectDropdownProps, ref) => {
    const defaultValuesCount = isValueDefinedAndArray(props.defaultValue) ? props.defaultValue.length : 0;
    const valuesCount = isValueDefinedAndArray(props.value) ? props.value.length : defaultValuesCount;

    const [totalSelected, setTotalSelected] = useState(valuesCount);
    const [hideSelectedItems, setHideSelectedItems] = useState(false);

    const handleOnChange = (value: (string | BaseDropdownOption)[] | null) => {
        if (props.onChange) {
            if (value === null) {
                props.onChange(null);
            } else {
                if (props.value === null || props.value === undefined) {
                    setTotalSelected(value.length);
                }

                props.onChange(value.map((val) => (typeof val === "string" ? val : val.value)));
            }
        }
    };

    useEffect(() => {
        if (isValueDefinedAndArray(props.value)) {
            setTotalSelected(props.value.length);
        }
    }, [props.value]);

    const filterOptions = createFilterOptions({
        stringify: (option: string | BaseDropdownOption) => (typeof option === "string" ? option : option.label),
    });

    return (
        <Autocomplete
            {...props}
            ref={ref}
            data-testid={props["data-testid"] ?? "multiselect-dropdown"}
            multiple={true}
            filterOptions={props.filterOptions ?? filterOptions}
            options={props.options as (string | BaseDropdownOption)[]}
            defaultValue={props.defaultValue as (string | BaseDropdownOption)[]}
            value={props.value as (string | BaseDropdownOption)[]}
            isOptionEqualToValue={(option: string | BaseDropdownOption, value: string | BaseDropdownOption) =>
                typeof option === "string" ? value === option : (value as BaseDropdownOption).value === option.value
            }
            onChange={(_, value: (string | BaseDropdownOption)[] | null) => handleOnChange(value)}
            popupIcon={<ChevronDownIcon sx={{ color: "text.primary", "&:disabled": { color: "red" } }} />}
            clearIcon={<ClearIconWithCounter totalSelected={totalSelected} />}
            getOptionLabel={(option) => (typeof option === "string" ? option : option.value.toString())}
            onFocus={() => setHideSelectedItems(true)}
            onBlur={() => setHideSelectedItems(false)}
            renderGroup={(params) => <DropdownGroup {...params} />}
            renderOption={(props, option, { selected }) => <DropdownOption props={props} option={option} selected={selected} />}
            renderInput={(params) => <CustomInputField params={params} error={error} helperText={helperText} props={props} />}
            renderTags={(value: (string | BaseDropdownOption)[]) => (
                <SelectedOptions
                    values={getLabelsAsText(value)}
                    key={"tags-as-text"}
                    hideSelectedItems={hideSelectedItems}
                    size={props.size}
                />
            )}
            sx={{
                "& .MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root": {
                    pr: "75px !important",
                },
                "& .MuiInputAdornment-root": {
                    position: "absolute",
                    right: error ? "26px" : "0",
                    top: props.size === "small" ? "20px" : "30px",
                },
                ...props.sx,
            }}
            componentsProps={{
                paper: { sx: { fontSize: "14px !important" } },
            }}
        />
    );
});

const DropdownGroup = ({ group, key, children }: AutocompleteRenderGroupParams) => (
    <List key={"group-" + key} sx={{ padding: 0 }}>
        <Stack sx={{ paddingY: "0.5rem" }}>
            <Typography variant={"s1"}>{group}</Typography>
        </Stack>
        <List>{children}</List>
    </List>
);

const DropdownOption = ({
    props,
    option,
    selected,
}: {
    props: HTMLAttributes<HTMLLIElement> & { key: string };
    option: string | BaseDropdownOption;
    selected: boolean;
}) => {
    const { key, ...optionProps } = props;
    return (
        <ListItem key={"option-" + key} {...optionProps} style={{ paddingLeft: "1rem" }}>
            <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                <Typography variant={"s1"}>{typeof option === "string" ? option : option.label}</Typography>
                <SenecCheckbox checked={selected} hasError={false} />
            </Stack>
        </ListItem>
    );
};

const CustomInputField = ({
    params,
    error,
    helperText,
    props,
}: {
    params: AutocompleteRenderInputParams;
    error?: boolean;
    helperText?: string;
    props: MultiselectDropdownProps;
}) => {
    return (
        <SenecTextField
            {...params}
            variant={props.variant}
            size={props.size}
            data-testid={"input-with-label"}
            error={error}
            helperText={helperText}
            label={props.label}
            required={props.required}
        />
    );
};

const SelectedOptions = ({
    values,
    hideSelectedItems,
    size = "medium",
}: {
    values: string;
    hideSelectedItems: boolean;
    size?: "small" | "medium";
}) => {
    return (
        <Stack
            sx={{
                position: "relative",
                left: "8px",
                top: size === "small" ? "4px" : "20px",
                width: "100%",
                pr: "8px",
            }}
        >
            <Typography
                variant={"enbwTabletS1Medium"}
                data-testid={"selected-labels"}
                sx={{
                    display: hideSelectedItems ? "none" : "-webkit-box",
                    overflow: "hidden",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 1,
                }}
                style={{
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                }}
            >
                {values}
            </Typography>
        </Stack>
    );
};

const getLabelsAsText = (value: (string | BaseDropdownOption)[]) => {
    const labelWithSubCategory = (val: BaseDropdownOption): string => {
        if (val.subcategory) {
            return [val.subcategory, val.label].join(" ");
        } else {
            return val.label;
        }
    };

    const labels = value.map((val) => (typeof val === "string" ? val : labelWithSubCategory(val)));
    return labels.join(", ");
};

const ClearIconWithCounter = ({ totalSelected }: { totalSelected: number }) => {
    return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            {totalSelected > 0 && (
                <Typography variant="s1Medium" data-testid="multiselect-dropdown__selected-items-count">
                    {totalSelected}
                </Typography>
            )}
            <CloseSmallIcon sx={{ color: "senecSecondary.neutralGray40" }} />
        </Box>
    );
};

const isValueDefinedAndArray = (value: MultiselectDropdownProps["value"] | MultiselectDropdownProps["defaultValue"]) => {
    return value !== undefined && value !== null && Array.isArray(value);
};
